/**
 * Music Manager for background music playback
 */
class MusicManagerClass {
  private audio: HTMLAudioElement | null = null;
  private muted = false;
  private hasUserInteracted = false;
  private pendingPlay = false;

  constructor() {
    // Listen for first user interaction to enable audio
    const enableAudio = () => {
      this.hasUserInteracted = true;
      if (this.pendingPlay) {
        this.pendingPlay = false;
        this.play();
      }
      // Remove listeners after first interaction
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('touchstart', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };

    document.addEventListener('click', enableAudio);
    document.addEventListener('touchstart', enableAudio);
    document.addEventListener('keydown', enableAudio);
  }

  /**
   * Initialize and start playing intro music
   */
  play(): void {
    if (this.muted) return;

    // If user hasn't interacted yet, queue the play
    if (!this.hasUserInteracted) {
      this.pendingPlay = true;
      return;
    }

    if (!this.audio) {
      this.audio = new Audio('/music/intro.mp3');
      this.audio.loop = true;
      this.audio.volume = 0.5;
    }

    this.audio.play().catch((error) => {
      // Autoplay was blocked, will try again on user interaction
      console.log('Music autoplay blocked, waiting for user interaction:', error.message);
      this.pendingPlay = true;
    });
  }

  /**
   * Stop the music
   */
  stop(): void {
    if (this.audio) {
      this.audio.pause();
      this.audio.currentTime = 0;
    }
    this.pendingPlay = false;
  }

  /**
   * Pause the music (can be resumed)
   */
  pause(): void {
    if (this.audio) {
      this.audio.pause();
    }
  }

  /**
   * Resume paused music
   */
  resume(): void {
    if (this.audio && !this.muted) {
      this.audio.play().catch(() => {
        // Ignore errors
      });
    }
  }

  /**
   * Toggle mute state
   */
  toggleMute(): boolean {
    this.muted = !this.muted;

    if (this.audio) {
      if (this.muted) {
        this.audio.pause();
      } else {
        this.audio.play().catch(() => {
          // Ignore errors
        });
      }
    }

    // Persist preference
    try {
      localStorage.setItem('musicMuted', this.muted ? 'true' : 'false');
    } catch {
      // localStorage not available
    }

    return this.muted;
  }

  /**
   * Set mute state
   */
  setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.audio) {
      if (muted) {
        this.audio.pause();
      } else {
        this.audio.play().catch(() => {
          // Ignore errors
        });
      }
    }
  }

  /**
   * Check if music is muted
   */
  isMuted(): boolean {
    return this.muted;
  }

  /**
   * Load mute preference from localStorage
   */
  loadPreference(): void {
    try {
      const saved = localStorage.getItem('musicMuted');
      if (saved === 'true') {
        this.muted = true;
      }
    } catch {
      // localStorage not available
    }
  }

  /**
   * Check if music is currently playing
   */
  isPlaying(): boolean {
    return this.audio !== null && !this.audio.paused;
  }
}

export const MusicManager = new MusicManagerClass();
